import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Clock, Send, CheckCircle, MapPin, HardHat, Upload, X, FileText, Image } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { companyInfo, services, serviceAreas } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Phone number formatting utility
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  const limitedNumber = phoneNumber.slice(0, 10);
  
  if (limitedNumber.length === 0) return '';
  if (limitedNumber.length <= 3) return `(${limitedNumber}`;
  if (limitedNumber.length <= 6) return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3)}`;
  return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3, 6)}-${limitedNumber.slice(6)}`;
};

// Email validation utility
const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

// File size formatter
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Budget options for the slider with custom increments
// Starts at $0, then $5K increments up to $100K, $25K increments to $250K, $50K increments to $500K, $100K increments to $3M+
const budgetOptions = [
  // Starting point - requires user to move slider
  0,
  // $5K increments: $5K - $100K
  5000, 10000, 15000, 20000, 25000, 30000, 35000, 40000, 45000, 50000,
  55000, 60000, 65000, 70000, 75000, 80000, 85000, 90000, 95000, 100000,
  // $25K increments: $100K - $250K
  125000, 150000, 175000, 200000, 225000, 250000,
  // $50K increments: $250K - $500K
  300000, 350000, 400000, 450000, 500000,
  // $100K increments: $500K - $3M+
  600000, 700000, 800000, 900000, 1000000,
  1100000, 1200000, 1300000, 1400000, 1500000,
  1600000, 1700000, 1800000, 1900000, 2000000,
  2100000, 2200000, 2300000, 2400000, 2500000,
  2600000, 2700000, 2800000, 2900000, 3000000
];

// Format budget for display
const formatBudget = (value) => {
  if (value === 0) return '$0';
  if (value >= 3000000) return '$3M+';
  if (value >= 1000000) {
    const millions = value / 1000000;
    return millions % 1 === 0 ? `$${millions}M` : `$${millions.toFixed(1)}M`;
  }
  return `$${value.toLocaleString()}`;
};

// US States for dropdown
const usStates = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

const ProjectConsultationPage = () => {
  const [formData, setFormData] = useState({
    inquiryType: 'individual',
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    projectBudget: 0,
    service: '',
    message: '',
    sms_consent: false
  });
  const [files, setFiles] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  
  // Bot protection: Track when form was loaded
  const [formLoadTime] = useState(Date.now());
  // Honeypot field (hidden from users, bots will fill it)
  const [honeypot, setHoneypot] = useState('');

  // Allowed file types
  const allowedTypes = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/dwg', 'image/vnd.dwg', 'application/acad', 'application/x-acad',
    'application/zip', 'application/x-zip-compressed'
  ];
  const maxFileSize = 25 * 1024 * 1024; // 25MB per file
  const maxTotalSize = 100 * 1024 * 1024; // 100MB total

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Company name required if inquiry type is company
    if (formData.inquiryType === 'company' && !formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    
    // Job title required if inquiry type is company
    if (formData.inquiryType === 'company' && !formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required for company inquiries';
    }
    
    // Address fields validation
    if (!formData.address.trim()) {
      newErrors.address = 'Street address is required';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode.trim())) {
      newErrors.zipCode = 'Please enter a valid ZIP code';
    }
    
    if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      }
    }
    
    // Budget must be selected (not $0)
    if (formData.projectBudget === 0) {
      newErrors.projectBudget = 'Please select an estimated budget';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newErrors = { ...errors };
    delete newErrors.files;
    
    // Validate each file
    const validFiles = [];
    let totalSize = files.reduce((acc, f) => acc + f.size, 0);
    
    for (const file of selectedFiles) {
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(dwg|dxf)$/i)) {
        newErrors.files = 'Some files have unsupported formats. Allowed: Images, PDF, Word, Excel, CAD files, ZIP';
        continue;
      }
      
      if (file.size > maxFileSize) {
        newErrors.files = `File "${file.name}" exceeds 25MB limit`;
        continue;
      }
      
      if (totalSize + file.size > maxTotalSize) {
        newErrors.files = 'Total file size exceeds 100MB limit';
        break;
      }
      
      totalSize += file.size;
      validFiles.push(file);
    }
    
    setFiles([...files, ...validFiles]);
    setErrors(newErrors);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Bot protection checks
    const timeSpent = Date.now() - formLoadTime;
    
    // Check 1: If honeypot is filled, it's a bot (silently reject)
    if (honeypot) {
      console.log('Bot detected: honeypot filled');
      // Fake success to not alert bots
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          inquiryType: 'individual',
          companyName: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          jobTitle: '',
          address: '',
          address2: '',
          city: '',
          state: '',
          zipCode: '',
          projectBudget: 0,
          service: '',
          message: ''
        });
        setFiles([]);
      }, 3000);
      return;
    }
    
    // Check 2: If form submitted in less than 5 seconds, likely a bot (consultation form is longer)
    if (timeSpent < 5000) {
      console.log('Bot detected: form submitted too fast');
      toast.error('Please take your time filling out the form.', {
        description: 'Form submitted too quickly. Please try again.',
        duration: 5000,
      });
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      // Add bot protection fields
      submitData.append('_gotcha', honeypot);
      submitData.append('_timestamp', formLoadTime);
      
      // Add files
      files.forEach((file) => {
        submitData.append('files', file);
      });
      
      // Submit to project consultation endpoint
      await axios.post(`${BACKEND_URL}/api/consultation`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setIsSubmitted(true);
      
      // Show success toast
      toast.success('Consultation Request Submitted!', {
        description: 'Thank you for your interest! A team member will contact you within 24-48 business hours.',
        duration: 5000,
      });
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          inquiryType: 'individual',
          companyName: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          jobTitle: '',
          address: '',
          address2: '',
          city: '',
          state: '',
          zipCode: '',
          projectBudget: 0,
          service: '',
          message: '',
          sms_consent: false
        });
        setFiles([]);
      }, 3000);
    } catch (error) {
      console.error('Error submitting consultation request:', error);
      const errorMsg = error.response?.data?.detail || 'There was an error submitting your request. Please try again or call us directly.';
      toast.error('Submission Failed', {
        description: errorMsg,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'phone') {
      setFormData({ ...formData, [name]: formatPhoneNumber(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Clear job title when switching to individual
    if (name === 'inquiryType' && value === 'individual') {
      setFormData(prev => ({ ...prev, inquiryType: value, jobTitle: '' }));
    }
    
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) {
      return <Image className="w-5 h-5 text-cyan-600" />;
    }
    return <FileText className="w-5 h-5 text-slate-600" />;
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-1/2 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <HardHat className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Request a Project Consultation
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Ready to transform your space? Share your project details and our team will reach out to discuss your vision and provide a free estimate.
          </p>
        </div>
      </section>

      {/* Consultation Form Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info Sidebar - Shows second on mobile */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <h2 className="hidden lg:block text-2xl font-bold text-slate-900 mb-6">Project Consultation</h2>
              <p className="hidden lg:block text-slate-600 mb-8">
                Tell us about your project and we'll get back to you within 24 hours to discuss your consultation.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-rose-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Call Us</h3>
                    <a href={`tel:${companyInfo.phone}`} className="text-slate-600 hover:text-cyan-600">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Business Hours</h3>
                    <p className="text-slate-600">Mon - Fri: 8:00 AM - 5:00 PM</p>
                  </div>
                </div>
              </div>

              {/* Service Areas */}
              <div className="mt-10 pt-8 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-cyan-600" />
                  <h3 className="font-semibold text-slate-900">Service Areas</h3>
                </div>
                <p className="text-slate-600 text-sm mb-3">
                  Proudly serving homeowners throughout:
                </p>
                <div className="flex flex-wrap gap-2">
                  {serviceAreas.map((area) => (
                    <span key={area} className="text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                      {area}
                    </span>
                  ))}
                </div>
              </div>

              {/* Other Options */}
              <div className="mt-10 pt-8 border-t border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-4">Looking for something else?</h3>
                <div className="space-y-3">
                  <Link to="/contact" className="block text-cyan-600 hover:text-cyan-700 font-medium">
                    → General Inquiry
                  </Link>
                  <Link to="/vendor-application" className="block text-cyan-600 hover:text-cyan-700 font-medium">
                    → Vendor Application
                  </Link>
                  <Link to="/employment-application" className="block text-cyan-600 hover:text-cyan-700 font-medium">
                    → Employment Application
                  </Link>
                </div>
              </div>
            </div>

            {/* Consultation Form - Shows first on mobile */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h3>
                    <p className="text-slate-600">
                      We've received your consultation request and will get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot field - hidden from humans, bots will fill it */}
                    <div 
                      style={{ 
                        position: 'absolute', 
                        left: '-9999px', 
                        top: '-9999px',
                        opacity: 0, 
                        height: 0, 
                        width: 0,
                        overflow: 'hidden',
                        pointerEvents: 'none'
                      }} 
                      aria-hidden="true"
                    >
                      <label htmlFor="company_website">Company Website URL</label>
                      <input
                        type="text"
                        id="company_website"
                        name="company_website"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>
                    
                    {/* Inquiry Type Selection */}
                    <div>
                      <Label className="text-slate-700 mb-3 block">I am inquiring as: *</Label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="inquiryType"
                            value="individual"
                            checked={formData.inquiryType === 'individual'}
                            onChange={handleChange}
                            className="w-4 h-4 text-cyan-600 border-slate-300 focus:ring-cyan-500"
                          />
                          <span className="text-slate-700">An Individual</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="inquiryType"
                            value="company"
                            checked={formData.inquiryType === 'company'}
                            onChange={handleChange}
                            className="w-4 h-4 text-cyan-600 border-slate-300 focus:ring-cyan-500"
                          />
                          <span className="text-slate-700">A Company / Organization</span>
                        </label>
                      </div>
                    </div>

                    {/* Company Name - shown only for company inquiries */}
                    {formData.inquiryType === 'company' && (
                      <div>
                        <Label htmlFor="companyName" className="text-slate-700 mb-2 block">Company Name *</Label>
                        <Input
                          id="companyName"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          required={formData.inquiryType === 'company'}
                          className={`h-12 ${errors.companyName ? 'border-red-500' : ''}`}
                        />
                        {errors.companyName && <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>}
                      </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="firstName" className="text-slate-700 mb-2 block">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className="h-12"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName" className="text-slate-700 mb-2 block">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          className="h-12"
                        />
                      </div>
                    </div>

                    {/* Job Title - shown only for company inquiries */}
                    {formData.inquiryType === 'company' && (
                      <div>
                        <Label htmlFor="jobTitle" className="text-slate-700 mb-2 block">Job Title *</Label>
                        <Input
                          id="jobTitle"
                          name="jobTitle"
                          value={formData.jobTitle}
                          onChange={handleChange}
                          required={formData.inquiryType === 'company'}
                          className={`h-12 ${errors.jobTitle ? 'border-red-500' : ''}`}
                        />
                        {errors.jobTitle && <p className="text-red-500 text-sm mt-1">{errors.jobTitle}</p>}
                      </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email" className="text-slate-700 mb-2 block">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className={`h-12 ${errors.email ? 'border-red-500' : ''}`}
                        />
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 mb-2 block">Phone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className={`h-12 ${errors.phone ? 'border-red-500' : ''}`}
                          maxLength={14}
                        />
                        {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                      </div>
                    </div>

                    {/* Project Address Fields */}
                    <div className="space-y-4">
                      <h3 className="font-medium text-slate-800">Project Location</h3>
                      
                      <div>
                        <Label htmlFor="address" className="text-slate-700 mb-2 block">Street Address *</Label>
                        <Input
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          required
                          className={`h-12 ${errors.address ? 'border-red-500' : ''}`}
                        />
                        {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                      </div>
                      
                      <div>
                        <Label htmlFor="address2" className="text-slate-700 mb-2 block">Address Line 2</Label>
                        <Input
                          id="address2"
                          name="address2"
                          value={formData.address2}
                          onChange={handleChange}
                          className="h-12"
                        />
                      </div>
                      
                      <div className="grid sm:grid-cols-6 gap-4">
                        <div className="sm:col-span-3">
                          <Label htmlFor="city" className="text-slate-700 mb-2 block">City *</Label>
                          <Input
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            required
                            className={`h-12 ${errors.city ? 'border-red-500' : ''}`}
                          />
                          {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                        </div>
                        
                        <div className="sm:col-span-1">
                          <Label htmlFor="state" className="text-slate-700 mb-2 block">State *</Label>
                          <Select
                            value={formData.state}
                            onValueChange={(value) => {
                              setFormData({ ...formData, state: value });
                              if (errors.state) {
                                setErrors({ ...errors, state: null });
                              }
                            }}
                          >
                            <SelectTrigger className={`h-12 ${errors.state ? 'border-red-500' : ''}`}>
                              <SelectValue placeholder="" />
                            </SelectTrigger>
                            <SelectContent>
                              {usStates.map((state) => (
                                <SelectItem key={state.value} value={state.value}>
                                  {state.value}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                        </div>
                        
                        <div className="sm:col-span-2">
                          <Label htmlFor="zipCode" className="text-slate-700 mb-2 block">ZIP Code *</Label>
                          <Input
                            id="zipCode"
                            name="zipCode"
                            value={formData.zipCode}
                            onChange={handleChange}
                            required
                            maxLength={10}
                            className={`h-12 ${errors.zipCode ? 'border-red-500' : ''}`}
                          />
                          {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
                        </div>
                      </div>
                    </div>

                    {/* Project Budget Slider */}
                    <div>
                      <Label className="text-slate-700 mb-2 block">Estimated Project Budget</Label>
                      <div className={`bg-slate-50 rounded-xl p-6 ${errors.projectBudget ? 'ring-2 ring-red-500' : ''}`}>
                        <div className="flex justify-center items-center mb-4">
                          <span className={`text-3xl font-bold ${formData.projectBudget === 0 ? 'text-slate-400' : 'text-cyan-600'}`}>
                            {formatBudget(formData.projectBudget)}
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max={budgetOptions.length - 1}
                          value={budgetOptions.indexOf(formData.projectBudget)}
                          onChange={(e) => {
                            const index = parseInt(e.target.value);
                            setFormData({ ...formData, projectBudget: budgetOptions[index] });
                            if (errors.projectBudget && budgetOptions[index] > 0) {
                              setErrors({ ...errors, projectBudget: null });
                            }
                          }}
                          className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-cyan-600"
                        />
                        <div className="flex justify-between mt-2">
                          <span className="text-xs text-slate-400">Smaller Projects</span>
                          <span className="text-xs text-slate-400">Larger Projects</span>
                        </div>
                      </div>
                      {errors.projectBudget && <p className="text-red-500 text-sm mt-2">{errors.projectBudget}</p>}
                    </div>

                    <div>
                      <Label htmlFor="service" className="text-slate-700 mb-2 block">Service Interested In *</Label>
                      <Select
                        value={formData.service}
                        onValueChange={(value) => setFormData({ ...formData, service: value })}
                        required
                      >
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.title}>
                              {service.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-slate-700 mb-2 block">Tell Us About Your Project *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Please describe your project, timeline, and any specific requirements..."
                      />
                    </div>

                    {/* File Upload Section */}
                    <div>
                      <Label className="text-slate-700 mb-2 block">Upload Plans, Images or Files</Label>
                      <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-cyan-400 transition-colors">
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.zip"
                          onChange={handleFileChange}
                          className="hidden"
                          id="file-upload"
                        />
                        <label htmlFor="file-upload" className="cursor-pointer">
                          <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                          <p className="text-slate-600 mb-1">
                            <span className="text-cyan-600 font-medium">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-sm text-slate-500">
                            Images, PDFs, Word, Excel, CAD files, or ZIP (Max 25MB per file, 100MB total)
                          </p>
                        </label>
                      </div>
                      {errors.files && <p className="text-red-500 text-sm mt-2">{errors.files}</p>}
                      
                      {/* File List */}
                      {files.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {files.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
                              <div className="flex items-center gap-3">
                                {getFileIcon(file)}
                                <div>
                                  <p className="text-sm font-medium text-slate-700 truncate max-w-[200px] sm:max-w-[300px]">
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="text-slate-400 hover:text-red-500 transition-colors"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                          <p className="text-sm text-slate-500 mt-2">
                            Total: {files.length} file(s), {formatFileSize(files.reduce((acc, f) => acc + f.size, 0))}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* SMS Consent Checkbox */}
                    {formData.phone && (
                      <div className="flex items-start gap-3 p-4 bg-slate-100 rounded-lg border border-slate-200">
                        <input
                          type="checkbox"
                          id="sms_consent"
                          name="sms_consent"
                          checked={formData.sms_consent}
                          onChange={handleChange}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                        />
                        <label htmlFor="sms_consent" className="text-sm text-slate-600 leading-relaxed">
                          I agree to receive SMS/text messages from C3 Contracting regarding my inquiry. 
                          Message & data rates may apply. Reply STOP to unsubscribe at any time.
                        </label>
                      </div>
                    )}

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold h-12"
                    >
                      {isSubmitting ? 'Sending...' : <><Send className="mr-2 w-5 h-5" /> Request Consultation</>}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectConsultationPage;
